import React, { useState, useRef, useEffect } from 'react';
import { Reveal } from './UI';
import SplitText from './SplitText';

const ITEMS = [
  {
    q: 'Do I need to buy new cameras?',
    a: 'No. TruVue AI connects to the CCTV you already have. If a camera can output a live feed, it can usually be connected — no matter which brand installed it. No rip-and-replace, no capital expenditure on hardware.',
  },
  {
    q: 'How long does setup take?',
    a: "Most single-site deployments are live within days of camera access being confirmed, not months. Multi-site rollouts are scoped around your shift patterns and zones so they don't disrupt production.",
  },
  {
    q: 'What does TruVue AI actually detect?',
    a: 'PPE violations, idle machinery, unauthorised zone access, blocked walkways, loitering, fire and smoke, attendance, theft and more — configured to your specific site, zones and compliance requirements.',
  },
  {
    q: 'Is our footage stored or shared anywhere?',
    a: 'You control retention and access. Footage and events stay under your policies — encrypted end to end, accessible only to roles you authorise. See the Security & Privacy section for the specifics.',
  },
  {
    q: 'What happens if the internet goes down on-site?',
    a: 'That depends on your deployment option. Ask us about edge or on-prem processing if connectivity is a known concern at your site — we can keep detection running locally even without a cloud connection.',
  },
  {
    q: 'Does this replace our security guards or safety officers?',
    a: "No — it's built to make them faster, not to replace them. TruVue AI flags what needs a human's attention so your team can spend their time there instead of scrubbing through footage or doing manual walk-throughs.",
  },
  {
    q: 'Can it work across multiple sites?',
    a: 'Yes — one dashboard across every site, configurable per zone and shift, with roll-up reporting for plant heads overseeing more than one location. Each site can have its own rules and alert thresholds.',
  },
];

function FaqItem({ q, a, index, isOpen, onToggle }) {
  const panelRef = useRef(null);
  const [maxH, setMaxH] = useState('0px');

  useEffect(() => {
    if (!panelRef.current) return;
    // Force a reflow so transition fires properly
    if (isOpen) {
      setMaxH(panelRef.current.scrollHeight + 'px');
    } else {
      // First set explicit height, then on next frame collapse to 0
      setMaxH(panelRef.current.scrollHeight + 'px');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setMaxH('0px'));
      });
    }
  }, [isOpen]);

  const num = String(index + 1).padStart(2, '0');

  return (
    <div className="faq-row">
      {/* Separator line — spans FULL width of the row */}
      <div className="faq-sep" />

      <button
        className="faq-trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="faq-num">{num}</span>
        <SplitText
          tag="span"
          text={q}
          delay={20}
          duration={0.7}
          ease="power2.out"
          splitType="words"
          from={{ opacity: 0, y: 12 }}
          to={{ opacity: 1, y: 0 }}
          threshold={0.1}
          rootMargin="-40px"
          textAlign="left"
          className="faq-q"
        />
        <span className={`faq-icon${isOpen ? ' faq-icon--open' : ''}`} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" className="faq-icon-v" />
            <line x1="5"  y1="12" x2="19" y2="12" />
          </svg>
        </span>
      </button>

      {/* Smooth height-animated panel */}
      <div
        className="faq-panel"
        style={{
          maxHeight: maxH,
          overflow: 'hidden',
          transition: 'max-height 0.42s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div ref={panelRef} className="faq-panel-inner">
          <SplitText
            tag="p"
            text={a}
            delay={10}
            duration={0.7}
            ease="power2.out"
            splitType="words"
            from={{ opacity: 0, y: 10 }}
            to={{ opacity: 1, y: 0 }}
            threshold={0.1}
            rootMargin="-40px"
            textAlign="left"
            className="faq-answer"
          />
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [open, setOpen] = useState(null);
  const toggle = (i) => setOpen(open === i ? null : i);

  return (
    <section id="faq" className="section section-cream">
      <style>{`
        #faq [class*="eyebrow"]::before,
        #faq [class*="eyebrow"]::after {
          content: none !important;
          display: none !important;
        }

        /* ── Page-width container ── */
        #faq .faq-outer {
          max-width: 1520px;
          margin: 0 auto;
          padding-inline: clamp(16px, 3vw, 24px);
        }

        /* ── Two-column layout ── */
        #faq .faq-layout {
          display: grid;
          grid-template-columns: 420px 1fr;
          align-items: start;
          min-height: 0;
        }

        /* ── Left panel ── */
        #faq .faq-left {
          position: sticky;
          top: 96px;
          padding-right: clamp(32px, 4vw, 64px);
          padding-top: 8px;
          /* The vertical dividing line lives here as a right border */
          border-right: 1px solid var(--border-cream, #DDD8D0);
          /* Extend the border to full section height */
          align-self: stretch;
        }

        #faq .faq-left-inner {
          position: sticky;
          top: 96px;
        }

        #faq .faq-title {
          font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
          font-size: clamp(26px, 3.2vw, 42px);
          font-weight: 700;
          color: var(--ink, #111);
          line-height: 1.18;
          margin: 0 0 14px;
        }

        #faq .faq-sub {
          font-size: clamp(13.5px, 1vw, 15.5px);
          color: var(--ink-soft, #6B6760);
          line-height: 1.65;
          margin: 0 0 36px;
          max-width: 300px;
        }

        #faq .faq-cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 15px;
          font-weight: 500;
          color: var(--ink, #111);
          text-decoration: none;
          border-bottom: 1.5px solid currentColor;
          padding-bottom: 2px;
          transition: opacity 0.2s;
        }
        #faq .faq-cta:hover { opacity: 0.5; }

        #faq .faq-dot {
          width: 15px;
          height: 15px;
          border-radius: 50%;
          border: 1.5px solid var(--heat, #E07050);
          flex-shrink: 0;
        }

        /* ── Right panel ── */
        #faq .faq-right {
          padding-left: clamp(28px, 4vw, 60px);
          padding-top: 8px;
        }

        /* ── Each FAQ row ── */
        #faq .faq-row {
          /* Nothing — separator handles the line */
        }

        /* Full-width separator line */
        #faq .faq-sep {
          width: 100%;
          height: 1px;
          background: var(--border-cream, #DDD8D0);
        }

        /* Last row gets a bottom line too */
        #faq .faq-row:last-child::after {
          content: '';
          display: block;
          width: 100%;
          height: 1px;
          background: var(--border-cream, #DDD8D0);
        }

        /* ── Trigger button ── */
        #faq .faq-trigger {
          width: 100%;
          background: none;
          border: none;
          padding: 22px 0;
          display: flex;
          align-items: center;
          gap: 16px;
          cursor: pointer;
          text-align: left;
        }
        #faq .faq-trigger:focus-visible {
          outline: 2px solid var(--heat, #E07050);
          outline-offset: 3px;
          border-radius: 2px;
        }

        #faq .faq-num {
          font-family: var(--font-mono, monospace);
          font-size: 11.5px;
          font-weight: 600;
          color: #7c3aed;
          letter-spacing: 0.06em;
          flex-shrink: 0;
          width: 22px;
        }

        #faq .faq-q {
          font-family: var(--font-body, sans-serif);
          font-size: clamp(14.5px, 1.15vw, 17px);
          font-weight: 400;
          color: var(--ink, #111);
          line-height: 1.35;
          flex: 1;
        }

        /* + icon */
        #faq .faq-icon {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
          color: var(--heat, #E07050);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.38s cubic-bezier(0.4, 0, 0.2, 1);
        }
        #faq .faq-icon svg {
          width: 18px;
          height: 18px;
        }
        /* Rotate whole icon 45deg → turns + into × */
        #faq .faq-icon--open {
          transform: rotate(45deg);
        }

        /* ── Answer panel ── */
        #faq .faq-panel-inner {
          padding-bottom: 22px;
        }

        #faq .faq-answer {
          margin: 0;
          padding-left: 38px;
          font-size: clamp(13.5px, 1vw, 15.5px);
          line-height: 1.72;
          color: var(--ink-soft, #6B6760);
          max-width: 600px;
        }

        /* ── Tablet ── */
        @media (max-width: 860px) {
          #faq .faq-layout {
            grid-template-columns: 1fr;
          }

          #faq .faq-left {
            position: static;
            border-right: none;
            border-bottom: 1px solid var(--border-cream, #DDD8D0);
            padding-right: 0;
            padding-bottom: 36px;
            margin-bottom: 36px;
            align-self: auto;
          }

          #faq .faq-left-inner {
            position: static;
          }

          #faq .faq-right {
            padding-left: 0;
          }

          #faq .faq-sub {
            max-width: 100%;
          }
        }

        /* ── Mobile ── */
        @media (max-width: 480px) {
          #faq .faq-trigger {
            padding: 18px 0;
          }

          #faq .faq-answer {
            padding-left: 30px;
          }
        }
      `}</style>

      <div className="faq-outer">
        <div className="faq-layout">

          {/* Left */}
          <div className="faq-left">
            <Reveal>
              <div className="faq-left-inner">
                <h2 className="faq-title">
                  <span>Frequently asked </span>
                  <span className="sh2-purple">questions</span>
                </h2>
                <SplitText
                  tag="p"
                  text="Clear answers to common questions about TruVue AI."
                  delay={12}
                  duration={0.7}
                  ease="power2.out"
                  splitType="words"
                  from={{ opacity: 0, y: 15 }}
                  to={{ opacity: 1, y: 0 }}
                  threshold={0.1}
                  rootMargin="-40px"
                  textAlign="left"
                  className="faq-sub"
                />
                <a
                  href="https://wa.me/919425074535"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="faq-cta"
                >
                  <span className="faq-dot" />
                  Get in touch
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right */}
          <div className="faq-right">
            {ITEMS.map((item, i) => (
              <Reveal key={i} delay={i * 50}>
                <FaqItem
                  index={i}
                  q={item.q}
                  a={item.a}
                  isOpen={open === i}
                  onToggle={() => toggle(i)}
                />
              </Reveal>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}